/**
 * Tenant locale profile helpers (country ≠ locale ≠ currency ≠ timezone).
 *
 * Absence of configuration.localeProfile resolves to Brazil defaults so
 * existing tenants need no mass migration. Prefer these helpers over
 * `if (country === 'PT')` scattered through product code.
 *
 * Capabilities (geoMode, marketingConsentDefault, …) come from
 * country-capabilities.js and are derived from country — not persisted.
 */

import {
  getCountryCapability,
  localeProfileDefaultsForCountry,
  normalizeCountryCode,
  requireCountryCode,
  SUPPORTED_CURRENCIES,
  SUPPORTED_LOCALES,
  SUPPORTED_PHONE_REGIONS,
  SUPPORTED_TAX_ID_TYPES,
  tryGetCountryCapability,
} from "./country-capabilities.js";

export const DEFAULT_LOCALE_PROFILE_BR = Object.freeze(localeProfileDefaultsForCountry("BR"));
export const DEFAULT_LOCALE_PROFILE_PT = Object.freeze(localeProfileDefaultsForCountry("PT"));
export const DEFAULT_LOCALE_PROFILE_ES = Object.freeze(localeProfileDefaultsForCountry("ES"));

const VALID_LOCALES = new Set(SUPPORTED_LOCALES);
const VALID_CURRENCIES = new Set(SUPPORTED_CURRENCIES);
const VALID_TAX_ID_TYPES = new Set(SUPPORTED_TAX_ID_TYPES);
const VALID_PHONE_REGIONS = new Set(SUPPORTED_PHONE_REGIONS);

function normalizeLocaleToken(raw) {
  const value = String(raw || "")
    .trim()
    .replace(/_/g, "-");
  const lowered = value.toLowerCase();
  if (lowered === "pt-br") return "pt-BR";
  if (lowered === "pt-pt") return "pt-PT";
  return value;
}

/**
 * Normalize a partial profile. Throws when an explicit country is unsupported.
 * @param {object|null|undefined} raw
 * @param {{ defaultCountry?: string }} [options]
 * @returns {typeof DEFAULT_LOCALE_PROFILE_BR}
 */
export function normalizeLocaleProfile(raw, options = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const defaultCountry = options.defaultCountry || "BR";

  let country;
  const rawCountry = source.country;
  if (rawCountry !== null && rawCountry !== undefined && String(rawCountry).trim() !== "") {
    country = requireCountryCode(rawCountry);
  } else {
    country = requireCountryCode(defaultCountry);
  }

  const defaults = localeProfileDefaultsForCountry(country);
  const locale = normalizeLocaleToken(source.locale || defaults.locale);
  const currency = String(source.currency || defaults.currency)
    .trim()
    .toUpperCase();
  const taxIdType = String(source.taxIdType || defaults.taxIdType)
    .trim()
    .toUpperCase();
  const phoneDefaultRegion = String(source.phoneDefaultRegion || defaults.phoneDefaultRegion)
    .trim()
    .toUpperCase();
  const timezone = String(source.timezone || defaults.timezone).trim() || defaults.timezone;
  const taxId = String(source.taxId ?? "").trim();

  return {
    country,
    locale: VALID_LOCALES.has(locale) ? locale : defaults.locale,
    currency: VALID_CURRENCIES.has(currency) ? currency : defaults.currency,
    timezone,
    taxIdType: VALID_TAX_ID_TYPES.has(taxIdType) ? taxIdType : defaults.taxIdType,
    taxId,
    phoneDefaultRegion: VALID_PHONE_REGIONS.has(phoneDefaultRegion)
      ? phoneDefaultRegion
      : defaults.phoneDefaultRegion,
  };
}

/**
 * @param {unknown} companyOrConfig - company item, configuration object, or
 *   an object that already embeds `localeProfile` (e.g. clinic info GET).
 * @returns {typeof DEFAULT_LOCALE_PROFILE_BR}
 */
export function getCompanyLocaleProfile(companyOrConfig) {
  if (!companyOrConfig || typeof companyOrConfig !== "object") {
    return { ...DEFAULT_LOCALE_PROFILE_BR };
  }

  let raw = null;
  if (companyOrConfig.localeProfile && typeof companyOrConfig.localeProfile === "object") {
    raw = companyOrConfig.localeProfile;
  } else if (
    companyOrConfig.configuration &&
    typeof companyOrConfig.configuration === "object" &&
    companyOrConfig.configuration.localeProfile &&
    typeof companyOrConfig.configuration.localeProfile === "object"
  ) {
    raw = companyOrConfig.configuration.localeProfile;
  }

  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_LOCALE_PROFILE_BR };
  }

  const explicitCaps = tryGetCountryCapability(raw.country);
  try {
    return normalizeLocaleProfile(raw);
  } catch {
    if (explicitCaps) {
      // Recognized country with corrupt sibling fields → country defaults (not BR).
      return localeProfileDefaultsForCountry(explicitCaps.country);
    }
    // Unsupported / unreadable legacy → BR operational fallback.
    return { ...DEFAULT_LOCALE_PROFILE_BR };
  }
}

/**
 * Build profile for an explicit country (ops / future signup).
 * @param {string} country
 * @param {object} [overrides]
 */
export function buildLocaleProfileForCountry(country, overrides = {}) {
  const normalizedCountry = requireCountryCode(country);
  return normalizeLocaleProfile(
    {
      ...localeProfileDefaultsForCountry(normalizedCountry),
      ...overrides,
      country: normalizedCountry,
    },
    { defaultCountry: normalizedCountry }
  );
}

/** @deprecated Prefer capability helpers; kept for existing imports. */
export function isPortugalTenant(profileOrCompany) {
  const profile =
    profileOrCompany &&
    typeof profileOrCompany === "object" &&
    profileOrCompany.country &&
    !profileOrCompany.configuration &&
    !profileOrCompany.localeProfile
      ? profileOrCompany
      : getCompanyLocaleProfile(profileOrCompany);
  return String(profile?.country || "").toUpperCase() === "PT";
}

export function isSpainTenant(profileOrCompany) {
  const profile =
    profileOrCompany &&
    typeof profileOrCompany === "object" &&
    profileOrCompany.country &&
    !profileOrCompany.configuration &&
    !profileOrCompany.localeProfile
      ? profileOrCompany
      : getCompanyLocaleProfile(profileOrCompany);
  return String(profile?.country || "").toUpperCase() === "ES";
}

export function getPhoneDefaultRegion(profileOrCompany) {
  const profile = getCompanyLocaleProfile(profileOrCompany);
  return profile.phoneDefaultRegion || "BR";
}

/**
 * Resolve the tenant phone region from cached company configuration.
 * Falls back to BR when cache is missing (legacy tenants).
 * @returns {"BR"|"PT"|"ES"|string}
 */
export function getTenantPhoneRegion() {
  return getTenantLocaleProfile().phoneDefaultRegion || "BR";
}

/**
 * Resolve the full tenant locale profile from cached company configuration.
 * Falls back to BR defaults when cache is missing.
 * @returns {typeof DEFAULT_LOCALE_PROFILE_BR}
 */
export function getTenantLocaleProfile() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return { ...DEFAULT_LOCALE_PROFILE_BR };
  }
  try {
    const companyId =
      localStorage.getItem(window.CONFIG?.STORAGE_KEYS?.COMPANY_ID || "auris_company_id") || "";
    const baseKey =
      window.CONFIG?.STORAGE_KEYS?.COMPANY_CONFIGURATION || "auris_company_configuration";
    const scopedKey = companyId ? `${baseKey}:${companyId}` : baseKey;
    const raw = localStorage.getItem(scopedKey) || localStorage.getItem(baseKey) || "";
    if (!raw) return { ...DEFAULT_LOCALE_PROFILE_BR };
    const config = JSON.parse(raw);
    return getCompanyLocaleProfile(config);
  } catch {
    return { ...DEFAULT_LOCALE_PROFILE_BR };
  }
}

/**
 * @returns {"BRL"|"EUR"|string}
 */
export function getTenantCurrency() {
  return getTenantLocaleProfile().currency || "BRL";
}

/**
 * @returns {"pt-BR"|"pt-PT"|string}
 */
export function getTenantLocale() {
  return getTenantLocaleProfile().locale || "pt-BR";
}

/**
 * Creation default for allowsMarketing checkboxes (capability-driven).
 * @returns {boolean}
 */
export function getDefaultAllowsMarketing() {
  const profile = getTenantLocaleProfile();
  const caps = tryGetCountryCapability(profile.country);
  if (!caps) return true;
  return Boolean(caps.marketingConsentDefault);
}

/**
 * Dial prefix for UI (without national subscriber digits).
 * ES dial prefix is registered for display; full masks land in Phase 3.
 * @param {"BR"|"PT"|"ES"|string} [region]
 * @returns {string} e.g. "+55", "+351", or "+34"
 */
export function getPhoneDialPrefix(region) {
  const resolved = String(region || getTenantPhoneRegion() || "BR").toUpperCase();
  if (resolved === "PT") return "+351";
  if (resolved === "ES") return "+34";
  return "+55";
}

export { getCountryCapability, normalizeCountryCode, requireCountryCode };
